<template>
  <div
    v-editable="blok"
    class="cm-media-section"
    :class="[
      `is-variant-${blok.component.replace('cm_media--section-', '')}`,
      {
        'is-mirrored': 'is_mirrored' in blok && blok.is_mirrored === true,
        'is-image-full-width': 'is_image_full_width' in blok && blok.is_image_full_width === true,
      },
    ]">
    <!-- Exception for variant C with image full-width -->
    <template v-if="blok.component === 'cm_media--section-1-large'">
      <CMMediaCardWide
        v-if="blok.item_1?.[0].is_image_full_width === true"
        class="card-1"
        :youtubePrivacyWarning="settings?.youtube_privacy_warning"
        :item="blok.item_1[0]"
        :breakpointsWidthMap="firstEntryBreakpoints!" />
      <CMMediaCard
        v-else
        class="card-1"
        :item="{
          ...blok.item_1?.[0],
          component: 'cm_media--item',
          aspect_ratio: blok.item_1?.[0].aspect_ratio ? blok.item_1[0].aspect_ratio : '3-2', // This property is not optional in the original type, hence why we need to set a default here
        }"
        :youtubePrivacyWarning="settings?.youtube_privacy_warning"
        :mediaView="getCardMediaView(0)"
        :breakpointsWidthMap="firstEntryBreakpoints!" />
    </template>
    <template v-else>
      <!-- Item 1 -->
      <CMMediaCard
        v-if="'item_1' in blok && !!blok.item_1?.[0]"
        class="card-1"
        :youtubePrivacyWarning="settings?.youtube_privacy_warning"
        :item="blok.item_1?.[0]"
        :mediaView="getCardMediaView(0)"
        :breakpointsWidthMap="firstEntryBreakpoints!" />

      <!-- Item 2 -->
      <CMMediaCard
        v-if="'item_2' in blok && !!blok.item_2?.[0]"
        class="card-2"
        :youtubePrivacyWarning="settings?.youtube_privacy_warning"
        :item="blok.item_2?.[0]"
        :mediaView="getCardMediaView(1)"
        :breakpointsWidthMap="secondEntryBreakpoints!" />

      <!-- Item 3 -->
      <CMMediaCard
        v-if="'item_3' in blok && !!blok.item_3?.[0]"
        class="card-3"
        :youtubePrivacyWarning="settings?.youtube_privacy_warning"
        :item="blok.item_3?.[0]"
        :mediaView="getCardMediaView(2)"
        :breakpointsWidthMap="thirdEntryBreakpoints!" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import CMMediaCard from '~/components/storyblok/content/CMMedia/CMMediaCard.vue';
import CMMediaCardWide from '~/components/storyblok/content/CMMedia/CMMediaCardWide.vue';
import type { CmMediaStoryblok, CoreSettingsStoryblok } from '~/types/storyblok-generated';

const props = defineProps<{
  blok: NonNullable<CmMediaStoryblok['body']>[number];
  settings: CoreSettingsStoryblok;
  isDark?: boolean;
}>();

/**
 * Overview of variants:
 *
 * - Variant A: cm_media--section-1-large-1-small
 * - Variant B: cm_media--section-1-large-1-medium
 * - Variant C: cm_media--section-1-large
 * - Variant D: cm_media--section-2-small-1-medium
 * - Variant F: cm_media--section-3-medium
 * - Variant G: cm_media--section-2-medium
 */

const defaultMobileBreakpointsSmall = { '0': 484, '576': 646, '768': 861 };
const defaultMobileBreakpointsLarge = { '0': 529, '576': 706, '768': 941 };

type VariantType = NonNullable<CmMediaStoryblok['body']>[number]['component'];

const breakpoints: Record<
  VariantType,
  {
    default?: Record<number, number>;
    imageFullWidth?: Record<number, number>;
    small?: Record<number, number>;
    medium?: Record<number, number>;
    large?: Record<number, number>;
  }
> = {
  'cm_media--section-1-large-1-small': {
    // Missing mobile breakpoints because they differ between entries
    small: { '1024': 282, '1280': 424, '1920': 879, '2440': 732 },
    large: { '1024': 580, '1280': 871, '1920': 1332, '2440': 1218 },
  },
  'cm_media--section-1-large-1-medium': {
    // Missing mobile breakpoints because they differ between entries
    small: { '1024': 433, '1280': 649, '1920': 830, '2440': 999 },
    large: { '1024': 731, '1280': 1097, '1920': 1396, '2440': 1449 },
  },
  'cm_media--section-1-large': {
    default: { ...defaultMobileBreakpointsLarge, '1024': 680, '1280': 1020, '1920': 1299, '2440': 1110 },
    imageFullWidth: { ...defaultMobileBreakpointsLarge, '1024': 978, '1280': 1467, '1920': 1866, '2440': 1596 },
  },
  'cm_media--section-2-small-1-medium': {
    small: { ...defaultMobileBreakpointsSmall, '1024': 282, '1280': 423, '1920': 543, '2440': 462 },
    medium: { ...defaultMobileBreakpointsLarge, '1024': 433, '1280': 649, '1920': 830, '2440': 732 },
  },
  'cm_media--section-3-medium': {
    default: { ...defaultMobileBreakpointsLarge, '1024': 382, '1280': 573, '1920': 732, '2440': 624 },
  },
  'cm_media--section-2-medium': {
    default: { ...defaultMobileBreakpointsLarge, '1024': 430, '1280': 645, '1920': 823, '2440': 679 },
  },
};

const firstEntryBreakpoints = computed(() => {
  switch (props.blok.component) {
    case 'cm_media--section-1-large-1-small':
    case 'cm_media--section-1-large-1-medium':
      return {
        ...(props.blok.is_mirrored === true ? defaultMobileBreakpointsSmall : defaultMobileBreakpointsLarge),
        ...breakpoints[props.blok.component][props.blok.is_mirrored === true ? 'small' : 'large'],
      };
    case 'cm_media--section-2-small-1-medium':
      return breakpoints[props.blok.component][props.blok.is_mirrored === true ? 'medium' : 'small'];
    case 'cm_media--section-1-large':
      return props.blok.item_1?.[0].is_image_full_width === true
        ? breakpoints[props.blok.component].imageFullWidth
        : breakpoints[props.blok.component].default;
    case 'cm_media--section-3-medium':
    case 'cm_media--section-2-medium':
      return breakpoints[props.blok.component].default;
    default:
      return null;
  }
});

const secondEntryBreakpoints = computed(() => {
  switch (props.blok.component) {
    case 'cm_media--section-1-large-1-small':
    case 'cm_media--section-1-large-1-medium':
      return {
        ...(props.blok.is_mirrored === true ? defaultMobileBreakpointsLarge : defaultMobileBreakpointsSmall),
        ...breakpoints[props.blok.component][props.blok.is_mirrored === true ? 'large' : 'small'],
      };
    case 'cm_media--section-2-small-1-medium':
      return breakpoints[props.blok.component].small;
    case 'cm_media--section-3-medium':
    case 'cm_media--section-2-medium':
      return breakpoints[props.blok.component].default;
    default:
      return null;
  }
});

const thirdEntryBreakpoints = computed(() => {
  switch (props.blok.component) {
    case 'cm_media--section-2-small-1-medium':
      return breakpoints[props.blok.component][props.blok.is_mirrored === true ? 'small' : 'medium'];
    case 'cm_media--section-3-medium':
      return breakpoints[props.blok.component].default;
    default:
      return null;
  }
});

function getCardMediaView(index: number) {
  if (props.blok.component === 'cm_media--section-1-large' && props.blok.item_1?.[0].is_image_full_width === false) {
    return 'horizontal';
  }

  if (props.blok.component === 'cm_media--section-1-large-1-medium') {
    if (props.blok.is_mirrored === true) {
      return index === 1 ? 'align-right' : 'align-left';
    } else {
      return index === 0 ? 'align-left' : 'align-right';
    }
  }

  if (props.blok.component === 'cm_media--section-2-small-1-medium') {
    if (props.blok.is_mirrored === false && index === 2) {
      return 'align-right';
    } else if (props.blok.is_mirrored === true && index === 0) {
      return 'align-left';
    }
  }

  return 'normal';
}
</script>

<style src="./CMMediaSection.scss" lang="scss" scoped />
